"use client";

import { useMemo, useState } from "react";
import { Plus, X, Check } from "lucide-react";
import { Card, CardBody } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { ItemIcon } from "@/components/icons/ItemIcon";
import { PageHeader } from "../_components/PageHeader";
import { BIS_SLOTS, GROUP_BY_ID, groupForSlot, isCatalyzableGroup } from "@/lib/bis-slots";
import { useLootCache } from "@/lib/loot-cache";
import {
  useCharState,
  setBiS,
  addBiSItem,
  removeBiSItem,
  setGroupSatisfied,
} from "@/lib/state";
import { CLASS_BY_ID, SPEC_BY_ID } from "@/lib/classes";
import { qualityTextClass, SLOT_LABEL, classColorVar } from "@/lib/wow";
import { cn } from "@/lib/utils";
import type { BiSMode, GroupID, Item } from "@/lib/types";

export default function BiSPage() {
  const state = useCharState();
  const cls = CLASS_BY_ID[state.classID];
  const specEntry = SPEC_BY_ID[state.playedSpecID];
  const bis = state.bis[state.playedSpecID] ?? {};

  const filledCount = Object.values(bis).filter((e) => e && e.mode !== "skip").length;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="BIS WISHLIST"
        title="Best in slot"
        subtitle={
          specEntry
            ? `For ${specEntry.spec.name} ${specEntry.classDef.name} · ${filledCount}/14 slots configured`
            : `${filledCount}/14 slots configured`
        }
      />

      <Card>
        <CardBody className="p-0">
          <div className="divide-y divide-border">
            {BIS_SLOTS.map((slot) => {
              const entry = bis[slot.id];
              return (
                <BiSRow
                  key={slot.id}
                  groupID={slot.id}
                  label={slot.label}
                  catalyzable={slot.catalyzable}
                  positions={slot.positions}
                  entryMode={entry?.mode ?? "skip"}
                  itemIDs={entry?.itemIDs ?? []}
                  satisfied={!!state.equippedMyth.satisfiedGroups[slot.id]}
                  classToken={cls?.token}
                />
              );
            })}
          </div>
        </CardBody>
      </Card>

      <p className="text-xs text-fg-subtle text-center">
        Bonus rolls always award Myth-track. Catalyzable slots that already hold a Myth-track item
        are excluded from hit calculations.
      </p>
    </div>
  );
}

function BiSRow({
  groupID,
  label,
  catalyzable,
  positions,
  entryMode,
  itemIDs,
  satisfied,
  classToken,
}: {
  groupID: GroupID;
  label: string;
  catalyzable: boolean;
  positions: 1 | 2;
  entryMode: BiSMode;
  itemIDs: number[];
  satisfied: boolean;
  classToken?: string;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const cache = useLootCache();
  const items = itemIDs.map((id) => cache.items[id]).filter(Boolean) as Item[];

  return (
    <div className="px-5 py-3.5">
      <div className="flex items-start gap-4">
        <div className="w-32 shrink-0 pt-0.5">
          <p className="text-sm font-medium text-fg">{label}</p>
          {catalyzable && (
            <p className="text-[10px] text-quality-epic font-mono uppercase tracking-wider mt-0.5">
              Catalyzable
            </p>
          )}
          {positions === 2 && (
            <p className="text-[10px] text-fg-subtle font-mono uppercase tracking-wider mt-0.5">
              Both slots
            </p>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <ModeChips
            mode={entryMode}
            catalyzable={catalyzable}
            onChange={(m) => {
              if (m === "skip") setBiS(groupID, null);
              else if (m === "catalyzed") setBiS(groupID, { mode: "catalyzed" });
              else setBiS(groupID, { mode: "specific", itemIDs: itemIDs });
            }}
          />

          {entryMode === "catalyzed" && (
            <label className="mt-2 inline-flex items-center gap-2 text-xs text-fg-muted cursor-pointer select-none">
              <input
                type="checkbox"
                className="h-3.5 w-3.5 accent-accent"
                checked={satisfied}
                onChange={(e) => setGroupSatisfied(groupID, e.target.checked)}
              />
              {positions === 2
                ? `Both ${label.toLowerCase()}s already at Myth-track (skip in calc)`
                : `${label} already at Myth-track (skip in calc)`}
            </label>
          )}

          {entryMode === "specific" && (
            <div className="mt-2 space-y-1">
              {items.length === 0 && (
                <p className="text-xs text-fg-subtle italic">No items selected yet.</p>
              )}
              {items.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-2 rounded-xs bg-surface-2/40 p-1.5"
                >
                  <ItemIcon iconUrl={item.iconUrl} iconName={item.iconName} quality={item.quality} size={28} />
                  <span className={cn("text-sm flex-1 truncate", qualityTextClass(item.quality))}>
                    {item.name}
                  </span>
                  <span className="text-[10px] text-fg-subtle font-mono">
                    {item.bossName ?? "—"}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeBiSItem(groupID, item.id)}
                    className="p-1 text-fg-subtle hover:text-danger transition-colors"
                    aria-label="Remove"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                className="inline-flex items-center gap-1.5 text-xs text-fg-muted hover:text-accent transition-colors mt-1"
              >
                <Plus className="h-3.5 w-3.5" /> Add item
              </button>
            </div>
          )}
        </div>
      </div>

      {pickerOpen && (
        <ItemPicker
          groupID={groupID}
          slotLabel={label}
          selectedIDs={itemIDs}
          onClose={() => setPickerOpen(false)}
          classToken={classToken}
        />
      )}
    </div>
  );
}

function ModeChips({
  mode,
  catalyzable,
  onChange,
}: {
  mode: BiSMode;
  catalyzable: boolean;
  onChange: (m: BiSMode) => void;
}) {
  const opts: { value: BiSMode; label: string; show: boolean }[] = [
    { value: "skip",       label: "Skip",       show: true },
    { value: "catalyzed",  label: "Catalyzed",  show: catalyzable },
    { value: "specific",   label: "Specific",   show: true },
  ];
  return (
    <div className="inline-flex items-center gap-0.5 rounded-sm border border-border bg-surface p-0.5">
      {opts.filter((o) => o.show).map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={cn(
            "h-7 px-2.5 rounded-xs text-xs font-mono uppercase tracking-wider transition-colors",
            mode === o.value
              ? "bg-surface-3 text-fg"
              : "text-fg-subtle hover:text-fg hover:bg-surface-2",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function ItemPicker({
  groupID,
  slotLabel,
  selectedIDs,
  onClose,
  classToken,
}: {
  groupID: GroupID;
  slotLabel: string;
  selectedIDs: number[];
  onClose: () => void;
  classToken?: string;
}) {
  const [query, setQuery] = useState("");
  const cache = useLootCache();
  const allItems = useMemo(() => {
    return Object.values(cache.items)
      .filter((item) => groupForSlot(item.slot) === groupID)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [cache, groupID]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allItems;
    return allItems.filter(
      (i) =>
        i.name.toLowerCase().includes(q) ||
        (i.bossName ?? "").toLowerCase().includes(q),
    );
  }, [allItems, query]);

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className={cn(
          "fixed z-50 left-1/2 top-[10vh] -translate-x-1/2 w-[640px] max-w-[95vw] max-h-[80vh]",
          "rounded-md border border-border-strong bg-surface shadow-[0_24px_60px_-20px_rgba(0,0,0,0.9)]",
          "flex flex-col",
        )}
      >
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div>
            <p className="eyebrow">Pick items</p>
            <p className="font-display text-base text-fg mt-0.5">{slotLabel}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>Done</Button>
        </div>
        <div className="px-5 py-3 border-b border-border">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search items, bosses..."
          />
        </div>
        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 && (
            <p className="px-5 py-8 text-center text-sm text-fg-subtle">
              No items match.
            </p>
          )}
          {filtered.map((item) => {
            const selected = selectedIDs.includes(item.id);
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  if (selected) removeBiSItem(groupID, item.id);
                  else addBiSItem(groupID, item.id);
                }}
                className={cn(
                  "w-full flex items-center gap-3 px-5 py-2 text-left",
                  "hover:bg-surface-2 transition-colors border-l-2",
                  selected
                    ? "bg-surface-2/60 border-l-accent"
                    : "border-l-transparent",
                )}
              >
                <ItemIcon iconUrl={item.iconUrl} iconName={item.iconName} quality={item.quality} size={28} />
                <span className="min-w-0 flex-1">
                  <span className={cn("block text-sm truncate", qualityTextClass(item.quality))}>
                    {item.name}
                  </span>
                  <span className="block text-[11px] text-fg-subtle font-mono uppercase tracking-wider truncate">
                    {SLOT_LABEL[item.slot] ?? item.slot}
                    {item.bossName ? ` · ${item.bossName}` : ""}
                    {item.isToken ? " · Tier" : ""}
                  </span>
                </span>
                {selected && <Check className="h-4 w-4 text-accent shrink-0" />}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}
